import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { aboutInfo } from '@/mocks/about';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');
  const [openMilestone, setOpenMilestone] = useState<number | null>(null);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const honeypot = (data.get('company_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们将尽快与您联系！');
      setFormData({ name: '', email: '', phone: '', message: '' });
      form.reset();
      setTimeout(() => { setFormStatus('idle'); setFormMessage(''); }, 5000);
      return;
    }

    data.delete('company_alt');
    setFormStatus('sending');
    setFormMessage('');

    try {
      const res = await fetch('https://readdy.ai/api/form/d9u4aof8l97n3eas3h20', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(data as unknown as Record<string, string>).toString()
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try { parsed = JSON.parse(responseText); } catch { /* not json */ }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们将尽快与您联系！');
        setFormData({ name: '', email: '', phone: '', message: '' });
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText || '提交失败';
        if (serverMsg.includes('spam')) {
          setFormStatus('success');
          setFormMessage('感谢您的留言，我们将尽快与您联系！');
          form.reset();
        } else {
          setFormStatus('error');
          setFormMessage(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后重试');
    }
    setTimeout(() => { setFormStatus('idle'); setFormMessage(''); }, 5000);
  };

  return (
    <>
      <ScrollToTop />
      <Header />
      <main className="min-h-screen bg-background-50">
        <section className="relative w-full h-[260px] md:h-[360px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20corporate%20office%20building%20exterior%20with%20warm%20golden%20hour%20lighting%2C%20sleek%20glass%20facade%2C%20professional%20atmosphere%2C%20elegant%20architectural%20design%2C%20clean%20minimal%20style&width=1600&height=600&seq=jmtt-about-hero&orientation=landscape"
            alt="关于JMTT"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/55"></div>
          <div className="relative z-10 text-center px-4">
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-white mb-3">
              关于 JMTT
            </h1>
            <p className="text-white/60 text-sm md:text-base max-w-lg mx-auto">
              源于初心，成于坚持 —— 了解JMTT的品牌故事与发展历程
            </p>
          </div>
        </section>

        <nav className="max-w-[1400px] mx-auto px-4 md:px-6 -mt-8 relative z-10" aria-label="面包屑导航">
          <ol className="flex items-center gap-2 text-xs text-foreground-500 bg-background-50 rounded-lg px-4 py-3 border border-background-200/70" itemScope itemType="https://schema.org/BreadcrumbList">
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer" itemProp="item">
                <span itemProp="name">首页</span>
              </Link>
              <meta itemProp="position" content="1" />
            </li>
            <li><span className="text-foreground-300">/</span></li>
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <span className="text-foreground-700 font-medium" itemProp="name">关于我们</span>
              <meta itemProp="position" content="2" />
            </li>
          </ol>
        </nav>

        <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold mb-4">
              {aboutInfo.brandStory.subtitle}
            </span>
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-6">
              {aboutInfo.brandStory.title}
            </h2>
            <div className="space-y-4 text-foreground-600 text-sm md:text-base leading-relaxed text-left">
              {aboutInfo.brandStory.paragraphs.map((p, idx) => (
                <p key={idx}>{p}</p>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 max-w-3xl mx-auto mb-16">
            {[
              { value: '500万+', label: '活跃用户' },
              { value: '99.9%', label: '服务可用率' },
              { value: '200+', label: '覆盖国家' },
              { value: '10亿+', label: '处理交易' }
            ].map((stat, idx) => (
              <div key={idx} className="text-center p-5 rounded-lg bg-background-100/70 border border-background-200/70">
                <div className="text-2xl md:text-3xl font-heading font-bold text-primary-600">{stat.value}</div>
                <div className="text-foreground-500 text-xs mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-100/70">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold mb-4">
                发展历程
              </span>
              <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-3">
                JMTT 里程碑
              </h2>
              <p className="text-foreground-600 text-sm max-w-lg mx-auto">
                从创立到领航，每一步都凝聚着JMTT团队的智慧与汗水
              </p>
            </div>

            <div className="max-w-3xl mx-auto">
              <div className="relative pl-8 border-l-2 border-background-300">
                {aboutInfo.milestones.map((m, idx) => (
                  <div key={idx} className="mb-6 last:mb-0 relative">
                    <div className={`absolute -left-[calc(2rem+5px)] top-0 w-3 h-3 rounded-full border-2 ${
                      openMilestone === idx
                        ? 'bg-primary-500 border-primary-500'
                        : 'bg-background-50 border-background-300'
                    }`}></div>
                    <button
                      onClick={() => setOpenMilestone(openMilestone === idx ? null : idx)}
                      className="w-full text-left cursor-pointer group"
                    >
                      <div className="flex items-baseline gap-3 mb-1">
                        <span className="text-xs text-foreground-400 font-medium whitespace-nowrap">{m.year} {m.month}</span>
                        <span className="font-heading font-semibold text-foreground-900 text-sm group-hover:text-primary-600 transition-colors">
                          {m.title}
                        </span>
                      </div>
                    </button>
                    <div className={`transition-all duration-300 overflow-hidden ${
                      openMilestone === idx ? 'max-h-32 opacity-100 mt-2' : 'max-h-0 opacity-0'
                    }`}>
                      <p className="text-foreground-500 text-xs leading-relaxed pl-[calc(3.5rem+12px)]">{m.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-semibold mb-4">
                荣誉资质
              </span>
              <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-3">
                JMTT 荣誉与认证
              </h2>
              <p className="text-foreground-600 text-sm max-w-lg mx-auto">
                行业权威认可，品质实力见证
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-4xl mx-auto">
              {aboutInfo.honors.map((honor) => (
                <div key={honor.id} className="p-5 rounded-lg bg-background-50 border border-background-200/70 flex items-start gap-4">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                    <i className="ri-award-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-foreground-900 text-sm mb-1">{honor.title}</h4>
                    <p className="text-foreground-400 text-xs">{honor.year} · {honor.issuer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="py-12 md:py-16 bg-background-100/70">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold mb-4">
                  联系我们
                </span>
                <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-3">
                  期待与您沟通
                </h2>
                <p className="text-foreground-600 text-sm max-w-lg mx-auto">
                  如有任何问题或合作意向，欢迎通过以下方式联系我们
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-5">
                  <div className="flex items-start gap-4 p-5 rounded-lg bg-background-50 border border-background-200/70">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                      <i className="ri-phone-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="font-heading font-semibold text-foreground-900 text-sm mb-1">客服热线</h4>
                      <p className="text-foreground-500 text-sm">{aboutInfo.contactInfo.phone}</p>
                      <p className="text-foreground-400 text-xs mt-0.5">{aboutInfo.contactInfo.workingHours}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-5 rounded-lg bg-background-50 border border-background-200/70">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="font-heading font-semibold text-foreground-900 text-sm mb-1">电子邮箱</h4>
                      <p className="text-foreground-500 text-sm">{aboutInfo.contactInfo.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-5 rounded-lg bg-background-50 border border-background-200/70">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="font-heading font-semibold text-foreground-900 text-sm mb-1">公司地址</h4>
                      <p className="text-foreground-500 text-sm">{aboutInfo.contactInfo.address}</p>
                    </div>
                  </div>
                </div>

                <div className="p-6 md:p-8 rounded-lg bg-background-50 border border-background-200/70">
                  <h3 className="font-heading font-semibold text-foreground-900 text-base mb-6">发送消息</h3>
                  <form onSubmit={handleSubmit} data-readdy-form="true" className="space-y-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-foreground-700 text-xs font-medium mb-1.5">姓名</label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                        className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-foreground-700 text-xs font-medium mb-1.5">邮箱</label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                        className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-phone" className="block text-foreground-700 text-xs font-medium mb-1.5">电话</label>
                      <input
                        id="contact-phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                        className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors"
                        placeholder="请输入您的电话（选填）"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-foreground-700 text-xs font-medium mb-1.5">留言</label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        value={formData.message}
                        onChange={(e) => setFormData((prev) => ({ ...prev, message: e.target.value }))}
                        className="w-full px-3.5 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors resize-none"
                        placeholder="请输入您的留言（最多500字）"
                      />
                      <p className="text-foreground-400 text-xs mt-1 text-right">{formData.message.length}/500</p>
                    </div>

                    <div className="antispam-field">
                      <label htmlFor="contact-company-alt">Company</label>
                      <input id="contact-company-alt" name="company_alt" type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                    </div>

                    <button
                      type="submit"
                      disabled={formStatus === 'sending'}
                      className="w-full px-6 py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white font-semibold text-sm rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap"
                    >
                      {formStatus === 'sending' ? (
                        <span className="flex items-center justify-center gap-2">
                          <i className="ri-loader-4-line animate-spin"></i>
                          发送中...
                        </span>
                      ) : '发送消息'}
                    </button>

                    {formMessage && (
                      <div className={`text-sm p-3 rounded-lg ${
                        formStatus === 'success'
                          ? 'bg-accent-50 text-accent-700 border border-accent-200'
                          : 'bg-red-50 text-red-700 border border-red-200'
                      }`}>
                        {formMessage}
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>

        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: '关于JMTT',
          description: 'JMTT是您值得信赖的优质平台，了解品牌故事、发展历程与荣誉资质。',
          inLanguage: 'zh-CN',
          mainEntity: {
            '@type': 'Organization',
            name: 'JMTT',
            url: 'https://seedaojia.cn/',
            description: 'JMTT是您值得信赖的优质平台，提供全面的服务与解决方案。',
            contactPoint: {
              '@type': 'ContactPoint',
              telephone: '+86-400-888-0000',
              contactType: 'customer service',
              availableLanguage: 'Chinese'
            },
            address: {
              '@type': 'PostalAddress',
              streetAddress: '建国路88号JMTT大厦',
              addressLocality: '北京',
              addressRegion: '朝阳区',
              addressCountry: 'CN'
            }
          }
        }) }} />
      </main>
      <Footer />
    </>
  );
}